import { BaseModel } from "@/models/base.model"
import {
  BaseStoreFunctions,
  type IStoreFunctionsConfig
} from "@/store/base-functions/base-store-functions"
import ModelManager from "@/services/model-manager"
import { type IBase } from "@/interfaces/api/base.interface"
import { type ModelConstructorSignature } from "@/models/types/model-constructor-signature"
import { ModelFactory } from "@/factories/model-factory"

export class GenericStoreFunctions<
  Model extends BaseModel,
  IData extends IBase
> extends BaseStoreFunctions<Model, IData> {
  constructor(
    modelType: ModelConstructorSignature<IData, Model>,
    config: IStoreFunctionsConfig<Model> = {}
  ) {
    super(modelType, config)
  }

  public createModel<T extends Model | IData>(model: T): Promise<Model>
  public createModel<T extends (Model | IData)[]>(models: T): Promise<Model[]>
  public async createModel(
    modelOrModels: Model | IData | (Model | IData)[]
  ): Promise<Model | Model[]> {
    // Normalize input so we can handle both a single model or a list in one unified flow.
    const modelsArray = Array.isArray(modelOrModels) ? modelOrModels : [modelOrModels]
    const createdModels: Model[] = []

    // Create all models first using the ModelFactory.
    for (const model of modelsArray) {
      const mdl = ModelFactory.createModel(
        this.modelType,
        model as unknown as IData,
        this.config.managed
      )
      createdModels.push(mdl)
    }

    // Await any pending managed field resolutions before proceeding.
    if (this.config.managed) {
      await BaseModel.whenFullyResolved(createdModels)
    }

    // Invoke onCreate for each created model, or extend the collection.
    if (this.config.onCreate) {
      // Call the onCreate function for each created model.
      for (const mdl of createdModels) {
        this.config.onCreate!(mdl)
      }
    } else if (this.config.collection) {
      // Or we are dealing with a simple standalone helper, like persons or machines.
      const collection = this.config.collection.value
      const existingIds = new Set(collection.map((item) => item.id)) // Set for O(1) lookup.
      const toAdd: Model[] = []

      for (const mdl of createdModels) {
        const index = existingIds.has(mdl.id) ? 0 : -1
        if (index === -1) {
          // Add the model to the collection if it doesn't exist yet.
          toAdd.push(mdl)
        }
      }

      if (toAdd.length > 0) {
        // Perform a single reactive push to minimize reactive updates.
        collection.push(...toAdd)
      }
    }

    // Return shape compatible with previous single-model behavior.
    return Array.isArray(modelOrModels) ? createdModels : createdModels[0]
  }

  public updateModel(instance: Model | IData): Model {
    return ModelFactory.createModel(this.modelType, instance, this.config.managed)
  }

  public deleteModel(instance: Model): boolean {
    if (this.config.managed) {
      ModelManager.getInstance().delete(instance.id!)
    }

    if (this.config.onDelete) {
      this.config.onDelete(instance)
    } else if (this.config.collection) {
      /* Or we are dealing with a simple standalone helper, like users or machines */
      const index = this.config.collection.value.findIndex((item) => item.id === instance.id)
      if (index !== -1) {
        this.config.collection.value.splice(index, 1)
      }
    }
    return true
  }
}
